/**
 * WholesalerContext — Phase 5 (Multi-Tenant SaaS)
 *
 * Purpose:
 *   Fetches and caches the current user's wholesaler document from Firestore,
 *   making wholesaler branding, settings, and metadata available to any
 *   component in the tree via useWholesaler().
 *
 * Listener lifecycle:
 *   - Active only when the user is logged in AND has a wholesalerId token claim.
 *   - Tears down immediately on logout (isLoggedIn → false).
 *   - Rebuilds automatically if wholesalerId changes (e.g. after a retailer
 *     joins a wholesaler and calls AuthContext.refreshToken()).
 *   - Platform admins (wholesalerId === null) and unenrolled retailers both
 *     get wholesaler=null, loading=false — no Firestore read, zero cost.
 *
 * Consumer shape:
 *   const { wholesaler, loading } = useWholesaler();
 *
 * ISSUE-6 FIX: wholesaler document is now split into two Firestore documents:
 *
 *   /wholesalers/{wholesalerId}               ← public (isSignedIn)
 *     businessName, slug, city, state, logoUrl, status
 *
 *   /wholesalers/{wholesalerId}/private/config ← private (own tenant only)
 *     settings.*, inviteCode, plan, billingStatus, billingCycleEnd,
 *     trialEndsAt, gstIn, contactEmail, contactPhone, suspendReason,
 *     suspendedAt, suspendedBy, adminUids
 *
 * This context merges both snapshots so consumers see the same shape as
 * before. The user's own wholesalerId token claim satisfies
 * belongsToWholesaler(wholesalerId) in the Firestore rule for private/config,
 * so the read is authorised. Cross-tenant users only see the public fields
 * from the top-level document — the private/config rule denies them.
 *
 * Merged wholesaler shape (unchanged from before — consumers unaffected):
 *   {
 *     id:               string,          // same as the document ID (whl_xxx)
 *     businessName:     string,
 *     slug:             string,
 *     inviteCode:       string,          // from private/config
 *     logoUrl:          string | null,
 *     primaryColor:     string | null,
 *     city:             string,
 *     state:            string,
 *     status:           "onboarding" | "active" | "suspended",
 *     plan:             "trial" | "starter" | "growth" | "enterprise" | "legacy",
 *     settings: {                        // from private/config
 *       retailerApprovalEnabled: boolean,
 *       minimumOrderValue:       number,
 *       currency:                "INR",
 *       telegramChatId:          string | null,
 *     },
 *   }
 *
 * Usage in App.js (per-tenant approval gate):
 *   const { wholesaler } = useWholesaler();
 *   const approvalEnabled = wholesaler?.settings?.retailerApprovalEnabled ?? false;
 *
 * Wrapped in index.js inside AuthProvider (after AuthProvider resolves auth,
 * so wholesalerId is always populated correctly when this context starts).
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";

export const WholesalerContext = createContext(null);

export function WholesalerProvider({ children }) {
  const { wholesalerId, isLoggedIn } = useAuth();
  const [wholesaler, setWholesaler] = useState(null);
  const [loading, setLoading]       = useState(true);

  // Live tenant profile/settings listener. Retailer-facing catalog identity,
  // support contact, delivery rules, and admin profile edits should update from
  // the tenant documents without app-specific hardcoding.
  const fetchWholesalerData = useCallback(async () => {
    if (!isLoggedIn || !wholesalerId) {
      setWholesaler(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [publicSnap, privateSnap] = await Promise.all([
        getDoc(doc(db, "wholesalers", wholesalerId)),
        getDoc(doc(db, "wholesalers", wholesalerId, "private", "config")).catch((err) => {
          console.error("WholesalerContext private/config fetch error:", err);
          return null;
        })
      ]);

      if (!publicSnap.exists()) {
        setWholesaler(null);
      } else {
        const publicData = { id: publicSnap.id, ...publicSnap.data() };
        const privateData = (privateSnap && privateSnap.exists()) ? privateSnap.data() : {};
        setWholesaler({ ...publicData, ...privateData });
      }
    } catch (err) {
      console.error("WholesalerContext fetch error:", err);
      setWholesaler(null);
    } finally {
      setLoading(false);
    }
  }, [wholesalerId, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !wholesalerId) {
      setWholesaler(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    let publicData = null;
    let privateData = {};

    const mergeAndSet = () => {
      if (!publicData) {
        setWholesaler(null);
      } else {
        setWholesaler({ ...publicData, ...privateData });
      }
      setLoading(false);
    };

    const publicRef = doc(db, "wholesalers", wholesalerId);
    const privateRef = doc(db, "wholesalers", wholesalerId, "private", "config");

    const unsubPublic = onSnapshot(publicRef, (snap) => {
      publicData = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      mergeAndSet();
    }, (err) => {
      console.error("WholesalerContext public fetch error:", err);
      setWholesaler(null);
      setLoading(false);
    });

    const unsubPrivate = onSnapshot(privateRef, (snap) => {
      privateData = snap.exists() ? snap.data() : {};
      mergeAndSet();
    }, (err) => {
      console.error("WholesalerContext private/config fetch error:", err);
      privateData = {};
      mergeAndSet();
    });

    return () => {
      unsubPublic();
      unsubPrivate();
    };
  }, [wholesalerId, isLoggedIn]);

  return (
    <WholesalerContext.Provider value={{ wholesaler, loading, refetch: fetchWholesalerData }}>
      {children}
    </WholesalerContext.Provider>
  );
}

/* ---------------- useWholesaler HOOK ---------------- */
export function useWholesaler() {
  const ctx = useContext(WholesalerContext);
  if (!ctx) throw new Error("useWholesaler must be used within WholesalerProvider");
  return ctx;
}
